"use client"

import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import CampaignService from '@/api/campaign.service';
import { Loader2, Plus, Mail, MessageSquare, Send, Trash2, Eye, BarChart2 } from 'lucide-react';
import { 
  Card, 
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import FormModal from '@/components/FormModal';
import CreateCampaignForm from './CreateCampaignForm';

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  
  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;
  
  useEffect(() => {
    if (businessId) {
      fetchCampaigns();
    }
  }, [businessId]);
  
  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const response = await CampaignService.getAllCampaigns(businessId);
      
      if (response.data.result === 'success') {
        console.log("response.data", response.data)
        setCampaigns(response.data.campaigns || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch campaigns",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching campaigns",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    
    try {
      const response = await CampaignService.deleteCampaign(campaignToDelete);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Campaign deleted successfully",
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete campaign",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the campaign",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setCampaignToDelete(null);
    }
  };
  
  const handleSendCampaign = async (campaignId: number) => {
    try {
      const response = await CampaignService.sendCampaignNow(campaignId, {status: 'send'});
      
      if (response.data.result === 'success') {
        // Optimistic update
        const updatedCampaigns = campaigns.map(campaign => 
          campaign.id === campaignId 
            ? { ...campaign, status: 'sent', sentAt: new Date().toISOString() } 
            : campaign
        );
        setCampaigns(updatedCampaigns);
        
        toast({
          title: "Success",
          description: "Campaign sent successfully",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to send campaign",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast({
        title: "Error",
        description: "An error occurred while sending the campaign",
        variant: "destructive",
      });
    }
  };
  
  const getCampaignTypeIcon = (type: string) => {
    if (!type) return <Mail className="w-4 h-4 text-gray-500" />;
    
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4 text-blue-500" />;
      case 'sms':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'whatsapp':
        return <MessageSquare className="w-4 h-4 text-green-600" />;
      default:
        return <Mail className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const filteredCampaigns = campaigns.filter(campaign => {
    if (activeTab === 'all') return true;
    return campaign.status === activeTab;
  });
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading campaigns...</p>
      </div>
    );
  }
  
  return (
    <div className="container py-6 mx-auto">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Marketing Campaigns</h1>
            <p className="text-muted-foreground mt-1">Create and manage your marketing campaigns</p>
          </div>
          
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            className="gap-1"
          >
            <Plus className="w-4 h-4" />
            Create Campaign
          </Button>
        </div>
        
        {/* Campaign List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Campaigns</CardTitle>
            <CardDescription>
              Manage all your marketing campaigns in one place
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 w-full md:w-[400px] mb-6">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
                <TabsTrigger value="sent">Sent</TabsTrigger>
              </TabsList>
              
              <TabsContent value={activeTab}>
                {filteredCampaigns.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      {activeTab === 'all' 
                        ? "No Campaigns Found" 
                        : `No ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Campaigns`}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {activeTab === 'all'
                        ? "You haven't created any campaigns yet."
                        : `You don't have any ${activeTab} campaigns at the moment.`}
                    </p>
                    <Button onClick={() => setIsCreateModalOpen(true)} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Campaign
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[140px]">Name</TableHead>
                          <TableHead className="w-[120px]">Type</TableHead>
                          <TableHead>Recipients</TableHead>
                          <TableHead className="w-[150px]">Status</TableHead>
                          <TableHead className="w-[180px]">Date</TableHead>
                          <TableHead className="w-[100px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCampaigns.map((campaign) => (
                          <TableRow key={campaign.id}>
                            <TableCell className="font-medium whitespace-nowrap">
                              {campaign.name}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getCampaignTypeIcon(campaign.channel)}
                                <span>{campaign.channel?.charAt(0).toUpperCase() + campaign.channel?.slice(1) || 'Unknown'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {campaign.recipientCount} recipients
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                !campaign.status ? 'default' :
                                campaign.status === 'draft' ? 'bg-gray-500 hover:bg-gray-600' : 
                                campaign.status === 'scheduled' ? 'bg-yellow-500 hover:bg-yellow-600' : 
                                campaign.status === 'sent' ? 'bg-green-500 hover:bg-green-600' : 'default'
                              }>
                                {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1) || 'Unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {campaign.status === 'sent' && campaign.dateCreated && (
                                <span>Sent {campaign.dateCreated}</span>
                              )}
                              {campaign.status === 'scheduled' && campaign.scheduledAt && (
                                <span>Scheduled for {format(new Date(campaign.scheduledAt), 'MMM dd, yyyy')}</span>
                              )}
                              {campaign.status === 'draft' && (
                                <span>Created {campaign.dateCreated}</span>
                              )}
                              {!campaign.status && campaign.dateCreated && (
                                <span>Created {campaign.dateCreated}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="w-4 h-4"
                                    >
                                      <circle cx="12" cy="12" r="1" />
                                      <circle cx="12" cy="5" r="1" />
                                      <circle cx="12" cy="19" r="1" />
                                    </svg>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem 
                                    className="flex items-center gap-2"
                                    onClick={() => {/* View campaign */}}
                                  >
                                    <Eye className="w-4 h-4" />
                                    View
                                  </DropdownMenuItem>
                                  
                                  {campaign.status && campaign.status !== 'sent' && (
                                    <DropdownMenuItem 
                                      className="flex items-center gap-2" 
                                      onClick={() => handleSendCampaign(campaign.id)}
                                    >
                                      <Send className="w-4 h-4" />
                                      Send Now
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {campaign.status && campaign.status === 'sent' && (
                                    <DropdownMenuItem 
                                      className="flex items-center gap-2"
                                      onClick={() => {/* View stats */}}
                                    >
                                      <BarChart2 className="w-4 h-4" />
                                      View Stats
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuItem 
                                    className="flex items-center gap-2 text-destructive focus:text-destructive"
                                    onClick={() => {
                                      setCampaignToDelete(campaign.publicId);
                                      setIsDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      {/* Create Campaign Modal */}
      <FormModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        title="Create New Campaign"
      >
        <CreateCampaignForm 
          businessId={businessId} 
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchCampaigns();
          }}
        />
      </FormModal>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the campaign and all its data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCampaign}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 